import { ChangeEvent, useCallback, useEffect, useState } from "react";

import {
  SelectData,
  Autocomplete,
  AutocompleteSearchData,
  InfoPanel,
  TextField,
  Select,
  SelectEvent,
  Checkbox,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { MinorityReligiousSelects } from "./components";
import {
  getInstitutionById,
  searchInstitutionNew,
} from "../../../api/Institution/InstitutionApi";
import { CountySelect, FormCard } from "../../../components";
import {
  Institution,
  InstitutionCodeBook,
  InstitutionCodeBookSearch,
  InstitutionContact,
  ValidationFailure,
} from "../../../models";
import {
  InstitutionLanguageModel,
  SchoolingModelEnum,
} from "../../../models/Institution/InstitutionLanguageModel";
import { CommercialProgram } from "../../../models/Request/CommercialProgram";
import {
  InstitutionChangeReason,
  Request,
} from "../../../models/Request/Request";
import { HierarchicalCodeBook } from "../../../models/Shared/HierarchicalCodeBook";
import {
  useGetReligiousAndMinorityInstitutionsQuery,
  useGetFirstAssignedInstitutionQuery,
  useGetInstitutionChangeReasonsQuery,
} from "../../../queries/dataQuery";
import { mapInstitutionDetailsToRequest } from "../../../utils/request-mapper";
import { determineInstitutionType } from "../InstitutionDataStep/utils";

export interface InstitutionDataProps {
  item: Request;
  primaryInstitution?: Institution;
  handleUpdate: (request: Request) => void;
  onIsReligiousOrNationalMinorityCheckboxChange?: (checked: boolean) => void;
  onOtherInstitutionCountyCheckboxChange?: (checked: boolean) => void;
  errors?: ValidationFailure[];
}

export const InstitutionData = ({
  item,
  primaryInstitution,
  handleUpdate,
  onIsReligiousOrNationalMinorityCheckboxChange,
  onOtherInstitutionCountyCheckboxChange,
  errors,
}: InstitutionDataProps) => {
  const { t } = useTranslation();

  const [secondaryInstitution, setSecondaryInstitution] = useState<
    Institution | undefined
  >();

  const [
    primaryInstitutionLanguageModelList,
    setPrimaryInstitutionLanguageModelList,
  ] = useState<InstitutionLanguageModel[]>([]);
  const [
    secondaryInstitutionLanguageModelList,
    setSecondaryInstitutionLanguageModelList,
  ] = useState<InstitutionLanguageModel[]>([]);

  const [isMinorityOrReligiousSchool, setIsMinorityOrReligiousSchool] =
    useState(false);
  const [isOtherInstitutionCounty, setIsOtherInstitutionCounty] =
    useState(false);
  const [selectedMinoritySchoolId, setSelectedMinoritySchoolId] = useState<
    number | null
  >(null);
  const [selectedReligiousSchoolId, setSelectedReligiousSchoolId] = useState<
    number | null
  >(null);
  const [initialRequestValues, setInitialRequestValues] = useState(item);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState(
    item?.institutionId
  );
  const [selectedCountyId, setSelectedCountyId] = useState<number | null>(null);
  const [selectedInstitutionProgramId, setSelectedInstitutionProgramId] =
    useState<number | null>(item.institutionProgramId ?? null);
  const { data: religiousAndMinorityInstitutions } =
    useGetReligiousAndMinorityInstitutionsQuery(
      primaryInstitution?.address?.countyId ?? 0
    );

  const hasReligiousAndMinorityInstitutions =
    (religiousAndMinorityInstitutions?.minorityInstitutions?.length ?? 0) > 0 ||
    (religiousAndMinorityInstitutions?.religiousInstitutions?.length ?? 0) > 0;

  const { data: firstAssignedInstitution } =
    useGetFirstAssignedInstitutionQuery({
      schoolCandidateId: item.schoolCandidateId,
      schoolYearId: item.schoolYearId!,
      hasMinorityOrReligiousSchool: hasReligiousAndMinorityInstitutions,
    });

  const { data: institutionChangeReasons } =
    useGetInstitutionChangeReasonsQuery();

  const [isOutsideEnrollmentArea, setIsOutsideEnrollmentArea] = useState(
    item.isOutsideEnrollmentArea ?? false
  );

  useEffect(() => {
    setIsOutsideEnrollmentArea(item.isOutsideEnrollmentArea ?? false);
  }, [item.isOutsideEnrollmentArea]);

  useEffect(() => {
    if (!firstAssignedInstitution || !primaryInstitution) return;

    const isDifferentInstitution =
      primaryInstitution.id !== firstAssignedInstitution.id;

    if (!isDifferentInstitution) return;

    setIsMinorityOrReligiousSchool(true);

    const { isMinoritySchool, isReligiousSchool, schoolId } =
      determineInstitutionType(
        primaryInstitution.id,
        religiousAndMinorityInstitutions
      );

    setSelectedMinoritySchoolId(isMinoritySchool ? schoolId : null);
    setSelectedReligiousSchoolId(isReligiousSchool ? schoolId : null);

    setInitialRequestValues((initReqVals) =>
      mapInstitutionDetailsToRequest(initReqVals, firstAssignedInstitution)
    );
  }, [
    religiousAndMinorityInstitutions,
    firstAssignedInstitution,
    primaryInstitution,
  ]);

  useEffect(() => {
    if (
      !secondaryInstitution?.address?.countyId ||
      excludeCountyIds.includes(secondaryInstitution?.address?.countyId)
    )
      return;

    setSelectedCountyId(secondaryInstitution?.address?.countyId);
    setIsOtherInstitutionCounty(true);
  }, [secondaryInstitution]);

  const handleMinorityOrReligiousSchoolChange = () => {
    const newFlag = !isMinorityOrReligiousSchool;
    setIsMinorityOrReligiousSchool(newFlag);
    onIsReligiousOrNationalMinorityCheckboxChange?.(newFlag);

    resetValuesOnSchoolChange(!newFlag);
  };

  const handleMinorityReligiousSchoolChange = (
    e: ChangeEvent<SelectEvent>,
    type: "minority" | "religious"
  ) => {
    const newSchoolId = Number(e.target.value);

    const isMinority = type === "minority";

    setSelectedMinoritySchoolId(isMinority ? newSchoolId : null);
    setSelectedReligiousSchoolId(!isMinority ? newSchoolId : null);

    handleIsOutsideEnrollmentAreaChange(false);

    // Only set the institution ID if it's a valid number
    if (!isNaN(newSchoolId)) {
      setSelectedInstitutionId(newSchoolId);
      setSelectedInstitutionProgramId(null);

      const req = { ...item, institutionId: newSchoolId };
      handleUpdate(req);
    }
  };

  const resetOnCountyChange = () => {
    setSelectedCountyId(null);
    setSecondaryInstitution(undefined);

    const r: Request = {
      ...item,
      outsideEnrollmentAreaInstitutionProgramId: null,
      outsideEnrollmentAreaInstitutionProgram: null,
      outsideEnrollmentAreaCommercialProgramId: null,
      languageSchoolingModels: [],
      foreignLanguages: [],
    };
    handleUpdate(r);
  };

  const resetValuesOnSchoolChange = (shouldReturnToInitialValues: boolean) => {
    let req = {
      ...item,
    };

    if (shouldReturnToInitialValues) {
      req = initialRequestValues;
      setSelectedInstitutionId(initialRequestValues.institutionId);
      setSelectedInstitutionProgramId(
        initialRequestValues.institutionProgramId || null
      );
    } else {
      req = {
        ...req,
        institutionProgramId: null,
        institutionProgram: null,
        languageSchoolingModels: [],
        foreignLanguages: [],
        electiveSubjects: [],
        mandatoryForeignLanguages: [],
      };

      setSelectedInstitutionProgramId(null);
      setSelectedMinoritySchoolId(null);
      setSelectedReligiousSchoolId(null);
    }

    handleUpdate(req);
  };

  useEffect(() => {
    const fetchInstitution = async () => {
      if (
        item.outsideEnrollmentAreaInstitutionProgram &&
        item.schoolYearId &&
        (!secondaryInstitution ||
          secondaryInstitution.id !==
            item.outsideEnrollmentAreaInstitutionProgram.institutionId)
      ) {
        const institution = await getInstitutionById(
          item.outsideEnrollmentAreaInstitutionProgram.institutionId,
          item.schoolYearId,
          true
        );
        setSecondaryInstitution(institution);
      }
    };
    fetchInstitution();
  }, [item.outsideEnrollmentAreaInstitutionProgram]);

  // Sets State variable primaryInstitutionLanguageModelList to available
  // items based on the selected institutionProgram. It does not update
  // item properties.
  useEffect(() => {
    if (primaryInstitution && selectedInstitutionProgramId) {
      const languageModelArr =
        primaryInstitution?.languageSchoolingModels?.filter(
          (v) =>
            primaryInstitution.programs.find(
              (p) => p.id === selectedInstitutionProgramId
            )?.isNationalMinorityProgram || v.modelId === SchoolingModelEnum.C
        ) ?? [];
      setPrimaryInstitutionLanguageModelList(languageModelArr);
    } else {
      setPrimaryInstitutionLanguageModelList([]);
    }
  }, [primaryInstitution, selectedInstitutionProgramId]);

  // Sets State variable secondaryInstitutionLanguageModelList to available
  // items based on the selected outsideEnrollmentAreaInstitutionProgram. It does not update
  // item properties.
  useEffect(() => {
    if (
      secondaryInstitution &&
      item.outsideEnrollmentAreaInstitutionProgramId
    ) {
      const languageModelArr =
        secondaryInstitution?.languageSchoolingModels?.filter(
          (v) =>
            secondaryInstitution.programs.find(
              (p) => p.id === item.outsideEnrollmentAreaInstitutionProgramId
            )?.isNationalMinorityProgram || v.modelId === SchoolingModelEnum.C
        ) ?? [];
      setSecondaryInstitutionLanguageModelList(languageModelArr);
    } else {
      setSecondaryInstitutionLanguageModelList([]);
    }
  }, [secondaryInstitution, item.outsideEnrollmentAreaInstitutionProgramId]);

  const getCommercialPrograms = (institution: Institution): CodeBook[] => {
    let arrayObjects = [] as CodeBook[];
    if (institution) {
      for (const [propertyKey, propertyValue] of Object.entries(
        CommercialProgram
      )) {
        const num = Number(propertyKey);
        if (Number.isNaN(Number(propertyKey))) {
          continue;
        }
        const tranKey = "commercialProgram." + propertyValue;
        const trans = t(tranKey);
        arrayObjects.push({ id: num, name: trans });
      }
      if (!(institution.institutionSchoolYearOther?.allDayClass ?? false))
        arrayObjects = arrayObjects.filter(
          (v) => v.id !== CommercialProgram.FullDayClasses
        );
      if (!(institution.institutionSchoolYearOther?.extendedStayClass ?? false))
        arrayObjects = arrayObjects.filter(
          (v) => v.id !== CommercialProgram.ExtendedStay
        );
    }
    return arrayObjects;
  };
  const getHasCommercialPrograms = (institution?: Institution): boolean => {
    return (
      (institution?.institutionSchoolYearOther?.allDayClass ?? false) ||
      (institution?.institutionSchoolYearOther?.extendedStayClass ?? false)
    );
  };
  const getInstitutionContactInformation = (institution: Institution) => {
    const contactInformationArr: InstitutionContact[] = [];
    if (institution.institutionSchoolYearOther?.telephoneNumber) {
      contactInformationArr.push({
        contactType: "Telefon",
        contactValue: institution.institutionSchoolYearOther.telephoneNumber,
      });
    }
    if (institution.institutionSchoolYearOther?.email) {
      contactInformationArr.push({
        contactType: "e-mail",
        contactValue: institution.institutionSchoolYearOther.email,
      });
    }
    return (
      <>
        {contactInformationArr.map((val) => (
          <Row>
            <Col md={2}>{val.contactType}</Col>
            <Col md={6}>{val.contactValue}</Col>
            <Col md={4}>{val.note}</Col>
          </Row>
        ))}
      </>
    );
  };

  const handlePrimaryInstituteProgramChange = (e: ChangeEvent<SelectEvent>) => {
    const programId = Number.parseInt(e.target.value);

    const program = primaryInstitution?.programs.find(
      (v) => v.id === programId
    );
    const r: Request = {
      ...item,
      institutionProgramId: programId,
      institutionProgram: program ?? null,
      languageSchoolingModels:
        item.languageSchoolingModels?.filter(
          (v) => v.parentId !== primaryInstitution?.id
        ) ?? [],
      foreignLanguages:
        item.foreignLanguages?.filter(
          (v) => v.parentId !== primaryInstitution?.id
        ) ?? [],
    };
    setSelectedInstitutionProgramId(programId);
    handleUpdate(r);
  };
  const handlePrimaryInstituteLanguageSchoolingModelsChange = (
    e: ChangeEvent<SelectEvent>
  ) => {
    const vals = e.target.value as number[];
    const arr =
      item.languageSchoolingModels?.filter(
        (v) => v.parentId !== primaryInstitution?.id
      ) ?? [];
    const langs = primaryInstitution?.languageSchoolingModels?.filter((v) =>
      vals.some((val) => v.id === val)
    );
    if (langs) {
      langs.map((lang) =>
        arr.push({
          id: lang.id,
          name: lang.name,
          parentId: primaryInstitution?.id,
        })
      );
    }
    const r: Request = {
      ...item,
      languageSchoolingModels: arr,
    };
    handleUpdate(r);
  };
  const handlePrimaryInstituteForeignLanguageChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const arr =
      item.foreignLanguages?.filter(
        (v) => v.parentId !== primaryInstitution?.id
      ) ?? [];
    const lang = primaryInstitution?.foreignLanguages?.find(
      (v) => v.id === val
    );
    if (lang) {
      arr.push({
        id: lang.id,
        name: lang.name,
        parentId: primaryInstitution?.id,
      });
      const r: Request = {
        ...item,
        foreignLanguages: arr,
      };
      handleUpdate(r);
    }
  };
  const handlePrimaryInstituteCommercialProgramSelectedChange = () => {
    const c = item.commercialProgramId;
    const r: Request = {
      ...item,
      commercialProgramId: c ? null : CommercialProgram.FullDayClasses,
    };
    handleUpdate(r);
  };
  const handlePrimaryInstituteCommercialProgramChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const r: Request = {
      ...item,
      commercialProgramId: val,
    };
    handleUpdate(r);
  };
  const handlePrimaryInstituteMandatoryForeignLanguageChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const arr =
      item.mandatoryForeignLanguages?.filter(
        (v) => v.parentId !== primaryInstitution?.id
      ) ?? [];
    const lang = primaryInstitution?.mandatoryForeignLanguages?.find(
      (v) => v.id === val
    );
    if (lang) {
      arr.push({
        id: lang.id,
        name: lang.name,
        parentId: primaryInstitution?.id,
      });
      const r: Request = {
        ...item,
        mandatoryForeignLanguages: arr,
      };
      handleUpdate(r);
    }
  };
  const handlePrimaryInstituteElectiveSubjectChange = (e: any) => {
    const val = e.target.value as number[];
    const arr =
      item.electiveSubjects?.filter(
        (v) => v.parentId !== primaryInstitution?.id
      ) ?? [];
    const sub = primaryInstitution?.electiveSubjects.filter(
      (v) => val.find((s) => s === v.id) !== undefined
    );
    if (sub) {
      sub.map((v) => {
        const newItem = {
          id: v.id,
          name: v.name,
          parentId: primaryInstitution?.id,
        } as HierarchicalCodeBook;
        arr?.push(newItem);
        return newItem;
      });
    }
    const r: Request = {
      ...item,
      electiveSubjects: arr,
    };
    handleUpdate(r);
  };
  const handleIsOutsideEnrollmentAreaChange = (value?: boolean) => {
    let val = !item.isOutsideEnrollmentArea;
    if (value !== undefined) {
      val = value;
    }

    const r: Request = {
      ...item,
      isOutsideEnrollmentArea: val,
      outsideEnrollmentAreaInstitutionProgramId: val
        ? item.outsideEnrollmentAreaInstitutionProgramId
        : null,
      outsideEnrollmentAreaCommercialProgramId: val
        ? item.outsideEnrollmentAreaCommercialProgramId
        : null,
      outsideEnrollmentAreaInstitutionProgram: val
        ? item.outsideEnrollmentAreaInstitutionProgram
        : null,
      electiveSubjects:
        item.electiveSubjects?.filter(
          (v) => v.parentId === primaryInstitution?.id
        ) ?? [],
      mandatoryForeignLanguages:
        item.mandatoryForeignLanguages?.filter(
          (v) => v.parentId === primaryInstitution?.id
        ) ?? [],
      foreignLanguages:
        item.foreignLanguages?.filter(
          (v) => v.parentId === primaryInstitution?.id
        ) ?? [],
      languageSchoolingModels:
        item.languageSchoolingModels?.filter(
          (v) => v.parentId === primaryInstitution?.id
        ) ?? [],
    };
    setIsOutsideEnrollmentArea(val);
    handleUpdate(r);
  };
  const handleInstitutionChangeReasonChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const r: Request = {
      ...item,
      institutionChangeReason: val,
    };
    handleUpdate(r);
  };
  const handleInstitutionChangeReasonOtherChange = (e: any) => {
    const val = e.target.value as string;
    const r: Request = {
      ...item,
      institutionChangeReasonOther: val,
    };
    handleUpdate(r);
  };

  const handleSelectCounty = (e: any) => {
    const val = Number.parseInt(e.target.value);
    setSelectedCountyId(val);

    if (isNaN(val)) resetOnCountyChange();
  };

  const handleInstitutionCountyChange = () => {
    const newFlag = !isOtherInstitutionCounty;
    setIsOtherInstitutionCounty(newFlag);
    onOtherInstitutionCountyCheckboxChange?.(newFlag);

    resetOnCountyChange();
  };

  const excludeCountyIds = primaryInstitution?.address?.countyId
    ? [primaryInstitution?.address?.countyId]
    : [];

  const handleSecondaryInstitutionChange = async (e: any) => {
    const institutionId = e as number;
    let institution = undefined;
    if (!isNaN(institutionId))
      institution = await getInstitutionById(institutionId, item.schoolYearId);
    setSecondaryInstitution(institution);

    // clear any secondary institution information.
    const r: Request = {
      ...item,
      outsideEnrollmentAreaInstitutionProgramId: null,
      outsideEnrollmentAreaCommercialProgramId: null,
      outsideEnrollmentAreaInstitutionProgram: null,
      electiveSubjects:
        item.electiveSubjects?.filter(
          (v) => v.parentId === primaryInstitution?.id
        ) ?? [],
      mandatoryForeignLanguages:
        item.mandatoryForeignLanguages?.filter(
          (v) => v.parentId === primaryInstitution?.id
        ) ?? [],
      foreignLanguages:
        item.foreignLanguages?.filter(
          (v) => v.parentId === primaryInstitution?.id
        ) ?? [],
      languageSchoolingModels:
        item.languageSchoolingModels?.filter(
          (v) => v.parentId === primaryInstitution?.id
        ) ?? [],
    };
    handleUpdate(r);
  };
  const handleSecondaryInstituteCommercialProgramSelectedChange = () => {
    const c = item.outsideEnrollmentAreaCommercialProgramId;
    const r: Request = {
      ...item,
      outsideEnrollmentAreaCommercialProgramId: c
        ? null
        : CommercialProgram.FullDayClasses,
    };
    handleUpdate(r);
  };
  const handleSecondaryInstituteCommercialProgramChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const r: Request = {
      ...item,
      outsideEnrollmentAreaCommercialProgramId: val,
    };
    handleUpdate(r);
  };
  const handleSecondaryInstituteProgramChange = (
    e: ChangeEvent<SelectEvent>
  ) => {
    const val = Number.parseInt(e.target.value);
    const program = secondaryInstitution?.programs.find((v) => v.id === val);
    const r: Request = {
      ...item,
      outsideEnrollmentAreaInstitutionProgramId: val,
      outsideEnrollmentAreaInstitutionProgram: program ?? null,
      languageSchoolingModels:
        item.languageSchoolingModels?.filter(
          (v) => v.parentId !== secondaryInstitution?.id
        ) ?? [],
      foreignLanguages:
        item.foreignLanguages?.filter(
          (v) => v.parentId !== secondaryInstitution?.id
        ) ?? [],
    };
    handleUpdate(r);
  };
  const handleSecondaryInstituteMandatoryForeignLanguageChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const arr =
      item.mandatoryForeignLanguages?.filter(
        (v) => v.parentId !== secondaryInstitution?.id
      ) ?? [];
    const lang = secondaryInstitution?.mandatoryForeignLanguages.find(
      (v) => v.id === val
    );
    if (lang) {
      arr.push({
        id: lang.id,
        name: lang.name,
        parentId: secondaryInstitution?.id,
      });
      const r: Request = {
        ...item,
        mandatoryForeignLanguages: arr,
      };
      handleUpdate(r);
    }
  };
  const handleSecondaryInstituteElectiveSubjectChange = (e: any) => {
    const val = e.target.value as number[];
    const arr =
      item.electiveSubjects?.filter(
        (v) => v.parentId !== secondaryInstitution?.id
      ) ?? [];
    const sub = secondaryInstitution?.electiveSubjects.filter(
      (v) => val.find((s) => s === v.id) !== undefined
    );
    if (sub) {
      sub.map((v) => {
        const newItem = {
          id: v.id,
          name: v.name,
          parentId: secondaryInstitution?.id,
        } as HierarchicalCodeBook;
        arr?.push(newItem);
        return newItem;
      });
    }
    const r: Request = {
      ...item,
      electiveSubjects: arr,
    };
    handleUpdate(r);
  };
  const handleSecondaryInstituteForeignLanguageChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const arr =
      item.foreignLanguages?.filter(
        (v) => v.parentId !== secondaryInstitution?.id
      ) ?? [];
    const lang = secondaryInstitution?.foreignLanguages?.find(
      (v) => v.id === val
    );
    if (lang) {
      arr.push({
        id: lang.id,
        name: lang.name,
        parentId: secondaryInstitution?.id,
      });
      const r: Request = {
        ...item,
        foreignLanguages: arr,
      };
      handleUpdate(r);
    }
  };
  const handleSecondaryInstituteLanguageSchoolingModelsChange = (e: any) => {
    const vals = e.target.value as number[];
    const arr =
      item.languageSchoolingModels?.filter(
        (v) => v.parentId !== secondaryInstitution?.id
      ) ?? [];
    const langs = secondaryInstitution?.languageSchoolingModels?.filter((v) =>
      vals.some((val) => v.id === val)
    );
    if (langs) {
      langs.map((lang) =>
        arr.push({
          id: lang.id,
          name: lang.name,
          parentId: secondaryInstitution?.id,
        })
      );
    }
    const r: Request = {
      ...item,
      languageSchoolingModels: arr,
    };
    handleUpdate(r);
  };
  const handleSecondaryInstitutionSearchChange = useCallback(
    async (aSearch: AutocompleteSearchData) => {
      let result: SelectData<InstitutionCodeBook> = {
        keyProp: "id",
        valueProp: "id",
        textProp: "name",
        data: [],
      };
      if (aSearch.text && aSearch.text.length >= 3) {
        const search: InstitutionCodeBookSearch = {
          search: aSearch.text,
          countyIds: selectedCountyId
            ? [selectedCountyId]
            : primaryInstitution?.address?.countyId
            ? [primaryInstitution?.address?.countyId]
            : undefined,
          onlyActive: true,
          includeOutsideEnrollmentArea: true,
          includeHandicapped: false,
          skipAuthorizationFilter: true,
        };
        const institutions = await searchInstitutionNew(search);
        result = {
          ...result,
          data: institutions.filter((i) => i.id !== primaryInstitution?.id),
        };
      } else if (aSearch.value) {
        const id = Number.parseInt(aSearch.value.toString());
        if (!isNaN(id)) {
          const search: InstitutionCodeBookSearch = {
            onlyActive: true,
            includeOutsideEnrollmentArea: true,
            includeHandicapped: false,
            ids: [id],
            skipAuthorizationFilter: true,
          };
          const institutions = await searchInstitutionNew(search);
          result = {
            ...result,
            data: institutions,
          };
        }
      }
      return result;
    },
    [selectedCountyId, primaryInstitution?.address?.countyId]
  );

  return (
    <FormCard cardTitle={t("request.institutionStep")}>
      <Row>
        <Col md={12}>
          <InfoPanel
            message={t("request.mainInfoMessageInstitutionProgram")}
            color="warning"
            fullWidth
          />
        </Col>
      </Row>
      <FormCard cardTitle={t("common.expertCommission.institution")}>
        <Row>
          <Col md={6}>
            <TextField
              label={t("common.institutionDetails.name")}
              value={primaryInstitution?.name}
              plaintext
              bold
            />
          </Col>
          {hasReligiousAndMinorityInstitutions && (
            <Col md={6}>
              <Checkbox
                label={t(
                  "common.institutionDetails.nationalMinorityOrReligiousSchoolSelect"
                )}
                checked={isMinorityOrReligiousSchool}
                onChange={handleMinorityOrReligiousSchoolChange}
                infoText={t(
                  "common.institutionDetails.nationalMinorityOrReligiousSchoolSelectInfo"
                )}
              />
            </Col>
          )}
        </Row>

        <Row>
          <Col md={4}>
            <TextField
              label={t("common.institutionDetails.countyName")}
              value={primaryInstitution?.address?.county}
              plaintext
              bold
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.institutionDetails.municipalityName")}
              value={primaryInstitution?.address?.municipality}
              plaintext
              bold
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.institutionDetails.townName")}
              value={primaryInstitution?.address?.town}
              plaintext
              bold
            />
          </Col>
        </Row>

        {isMinorityOrReligiousSchool && (
          <MinorityReligiousSelects
            selectedMinoritySchoolId={selectedMinoritySchoolId}
            handleMinorityReligiousSchoolChange={
              handleMinorityReligiousSchoolChange
            }
            selectedReligiousSchoolId={selectedReligiousSchoolId}
            religiousAndMinorityInstitutions={religiousAndMinorityInstitutions}
          />
        )}

        <Row>
          <Col md={6}>
            <Select
              required
              emptyOption={{ value: undefined, text: "" }}
              label={t("request.institutionProgram")}
              name={"programs"}
              value={selectedInstitutionProgramId}
              onChange={handlePrimaryInstituteProgramChange}
              selectData={codeBookToSelectData(
                primaryInstitution?.programs?.filter(
                  (v) => v.isEnrollmentModule && !v.isHandicappedProgram
                )
              )}
              errorText={
                errors?.find((e) => e.propertyName === "InstitutionProgramId")
                  ?.message
              }
            />
          </Col>
          {primaryInstitution &&
            primaryInstitution.programs.find(
              (v) => v.id === selectedInstitutionProgramId
            )?.isInternationalProgram && (
              <Col md={6}>
                <Select
                  emptyOption={{ value: undefined, text: "" }}
                  label={t("common.institutionDetails.straniJezici")}
                  name={"straniJezici"}
                  value={
                    item.mandatoryForeignLanguages?.find(
                      (v) => v.parentId === selectedInstitutionId
                    )?.id
                  }
                  onChange={handlePrimaryInstituteForeignLanguageChange}
                  selectData={codeBookToSelectData(
                    primaryInstitution?.foreignLanguages ?? []
                  )}
                  errorText={
                    errors?.find((e) => e.propertyName === "ForeignLanguages")
                      ?.message
                  }
                />
              </Col>
            )}
          {primaryInstitutionLanguageModelList.length > 0 && (
            <Col md={6}>
              <Select
                multiple
                emptyOption={{ value: undefined, text: "" }}
                label={t("request.languageSchoolingModel")}
                name={"schoolingModel"}
                value={item.languageSchoolingModels
                  ?.filter((v) => v.parentId === selectedInstitutionId)
                  .map((v) => v.id)}
                onChange={handlePrimaryInstituteLanguageSchoolingModelsChange}
                selectData={codeBookToSelectData(
                  primaryInstitutionLanguageModelList
                )}
                errorText={
                  errors?.find(
                    (e) => e.propertyName === "LanguageSchoolingModels"
                  )?.message
                }
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col md={6}>
            <Checkbox
              label={t("commercialProgram.ExtendedStay")}
              name={nameof<typeof item>("commercialProgramId")}
              checked={
                (item.commercialProgramId ?? undefined) !== undefined &&
                getHasCommercialPrograms(primaryInstitution)
              }
              onChange={handlePrimaryInstituteCommercialProgramSelectedChange}
              disabled={!getHasCommercialPrograms(primaryInstitution)}
            />
          </Col>
          {item.commercialProgramId && (
            <Col md={6}>
              <Select
                value={item.commercialProgramId}
                onChange={handlePrimaryInstituteCommercialProgramChange}
                selectData={codeBookToSelectData(
                  getCommercialPrograms(primaryInstitution!)
                )}
                errorText={
                  errors?.find((e) => e.propertyName === "CommercialProgramId")
                    ?.message
                }
              />
            </Col>
          )}
        </Row>
      </FormCard>
      <FormCard cardTitle={t("request.programSelection")}>
        <Row>
          <Col md={6}>
            <Select
              required
              label={t("common.institutionDetails.obavezniStraniJezik")}
              value={
                item.mandatoryForeignLanguages?.find(
                  (v) => v.parentId === selectedInstitutionId
                )?.id
              }
              onChange={handlePrimaryInstituteMandatoryForeignLanguageChange}
              selectData={codeBookToSelectData(
                primaryInstitution?.mandatoryForeignLanguages
              )}
              errorText={
                errors?.find(
                  (e) => e.propertyName === "MandatoryForeignLanguages"
                )?.message
              }
            />
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Select
              multiple
              label={t("common.institutionDetails.izborniPredmeti")}
              value={item.electiveSubjects
                ?.filter((v) => v.parentId === primaryInstitution?.id)
                .map((v) => v.id)}
              onChange={handlePrimaryInstituteElectiveSubjectChange}
              selectData={codeBookToSelectData(
                primaryInstitution?.electiveSubjects
              )}
              errorText={
                errors?.find((e) => e.propertyName === "ElectiveSubjects")
                  ?.message
              }
            />
          </Col>
        </Row>
      </FormCard>
      <FormCard cardTitle={t("request.contactInformation")}>
        {primaryInstitution
          ? getInstitutionContactInformation(primaryInstitution)
          : ""}
      </FormCard>
      <FormCard cardTitle={t("request.otherInstitution")}>
        <Row>
          <Col md={12}>
            <Checkbox
              label={t("request.otherInstitutionLabel")}
              name={nameof<typeof item>("isOutsideEnrollmentArea")}
              checked={isOutsideEnrollmentArea}
              onChange={(e) => handleIsOutsideEnrollmentAreaChange()}
              disabled={
                isMinorityOrReligiousSchool &&
                (!!selectedMinoritySchoolId || !!selectedReligiousSchoolId)
              }
            />
          </Col>
        </Row>
        {isOutsideEnrollmentArea && (
          <>
            <FormCard cardTitle={t("common.expertCommission.institution")}>
              <Row style={{ alignItems: "center" }}>
                <Col md={6}>
                  <Select
                    required
                    label={t("request.institutionChangeReason")}
                    value={item.institutionChangeReason}
                    onChange={handleInstitutionChangeReasonChange}
                    selectData={codeBookToSelectData(institutionChangeReasons)}
                    errorText={
                      errors?.find(
                        (e) => e.propertyName === "InstitutionChangeReason"
                      )?.message
                    }
                  />
                </Col>
                <Col md={6} style={{ marginTop: "1rem" }}>
                  <Checkbox
                    label={t("request.otherInstitutionCountyLabel")}
                    onChange={handleInstitutionCountyChange}
                    checked={isOtherInstitutionCounty}
                  />
                </Col>
                {isOtherInstitutionCounty && (
                  <Col md={6}>
                    <CountySelect
                      label={t("request.otherCountyLabel")}
                      name={"CountyId"}
                      onChange={handleSelectCounty}
                      excludeCountyIds={excludeCountyIds}
                      value={selectedCountyId ?? undefined}
                    />
                  </Col>
                )}
                {item.institutionChangeReason ===
                  InstitutionChangeReason.Other && (
                  <Col md={6}>
                    <TextField
                      required
                      label={t("request.institutionChangeReason")}
                      value={item.institutionChangeReasonOther ?? ""}
                      onChange={handleInstitutionChangeReasonOtherChange}
                      errorText={
                        errors?.find(
                          (e) =>
                            e.propertyName === "InstitutionChangeReasonOther"
                        )?.message
                      }
                    />
                  </Col>
                )}
              </Row>
              <Row>
                <Col md={6}>
                  <Autocomplete
                    label={t("common.schoolCandidateModel.institutionList")}
                    name={"institutionList"}
                    value={secondaryInstitution?.id}
                    getOptions={handleSecondaryInstitutionSearchChange}
                    onChange={(e) =>
                      handleSecondaryInstitutionChange(
                        Number.parseInt(e.target.value)
                      )
                    }
                  />
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <TextField
                    label={t("common.institutionDetails.countyName")}
                    value={secondaryInstitution?.address?.county}
                    plaintext
                    bold
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    label={t("common.institutionDetails.municipalityName")}
                    value={secondaryInstitution?.address?.municipality}
                    plaintext
                    bold
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    label={t("common.institutionDetails.townName")}
                    value={secondaryInstitution?.address?.town}
                    plaintext
                    bold
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Select
                    required
                    emptyOption={{ value: undefined, text: "" }}
                    label={t("request.institutionProgram")}
                    name={"programs"}
                    value={item.outsideEnrollmentAreaInstitutionProgramId}
                    onChange={handleSecondaryInstituteProgramChange}
                    selectData={codeBookToSelectData(
                      secondaryInstitution?.programs?.filter(
                        (v) => v.isEnrollmentModule && !v.isHandicappedProgram
                      )
                    )}
                    errorText={
                      errors?.find(
                        (e) =>
                          e.propertyName ===
                          "OutsideEnrollmentAreaInstitutionProgramId"
                      )?.message
                    }
                  />
                </Col>
                {secondaryInstitution &&
                  secondaryInstitution.programs.find(
                    (v) =>
                      v.id === item.outsideEnrollmentAreaInstitutionProgram?.id
                  )?.isInternationalProgram && (
                    <Col md={6}>
                      <Select
                        emptyOption={{ value: undefined, text: "" }}
                        label={t("common.institutionDetails.straniJezici")}
                        name={"straniJezici"}
                        value={
                          item.foreignLanguages?.find(
                            (v) => v.parentId === secondaryInstitution.id
                          )?.id
                        }
                        onChange={handleSecondaryInstituteForeignLanguageChange}
                        selectData={codeBookToSelectData(
                          secondaryInstitution?.foreignLanguages ?? []
                        )}
                        errorText={
                          errors?.find(
                            (e) => e.propertyName === "ForeignLanguages"
                          )?.message
                        }
                      />
                    </Col>
                  )}
                {secondaryInstitutionLanguageModelList.length > 0 && (
                  <Col md={6}>
                    <Select
                      multiple
                      emptyOption={{ value: undefined, text: "" }}
                      label={t("request.languageSchoolingModel")}
                      name={"schoolingModel"}
                      value={item.languageSchoolingModels
                        ?.filter((v) => v.parentId === secondaryInstitution?.id)
                        .map((v) => v.id)}
                      onChange={
                        handleSecondaryInstituteLanguageSchoolingModelsChange
                      }
                      selectData={codeBookToSelectData(
                        secondaryInstitutionLanguageModelList
                      )}
                      errorText={
                        errors?.find(
                          (e) => e.propertyName === "LanguageSchoolingModels"
                        )?.message
                      }
                    />
                  </Col>
                )}
              </Row>
              <Row>
                <Col md={6}>
                  <Checkbox
                    label={t("commercialProgram.ExtendedStay")}
                    name={nameof<typeof item>(
                      "outsideEnrollmentAreaCommercialProgramId"
                    )}
                    checked={
                      (item.outsideEnrollmentAreaCommercialProgramId ??
                        undefined) !== undefined &&
                      getHasCommercialPrograms(secondaryInstitution)
                    }
                    onChange={
                      handleSecondaryInstituteCommercialProgramSelectedChange
                    }
                    disabled={!getHasCommercialPrograms(secondaryInstitution)}
                  />
                </Col>
                {item.outsideEnrollmentAreaCommercialProgramId && (
                  <Col md={6}>
                    <Select
                      value={item.outsideEnrollmentAreaCommercialProgramId}
                      onChange={handleSecondaryInstituteCommercialProgramChange}
                      selectData={codeBookToSelectData(
                        getCommercialPrograms(secondaryInstitution!)
                      )}
                      errorText={
                        errors?.find(
                          (e) => e.propertyName === "CommercialProgramId"
                        )?.message
                      }
                    />
                  </Col>
                )}
              </Row>
            </FormCard>
            <FormCard cardTitle={t("request.programSelection")}>
              <Row>
                <Col md={6}>
                  <Select
                    required
                    label={t("common.institutionDetails.obavezniStraniJezik")}
                    value={
                      item.mandatoryForeignLanguages?.find(
                        (v) => v.parentId === secondaryInstitution?.id
                      )?.id
                    }
                    onChange={
                      handleSecondaryInstituteMandatoryForeignLanguageChange
                    }
                    selectData={codeBookToSelectData(
                      secondaryInstitution?.mandatoryForeignLanguages
                    )}
                    errorText={
                      errors?.find(
                        (e) => e.propertyName === "MandatoryForeignLanguages"
                      )?.message
                    }
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Select
                    multiple
                    label={t("common.institutionDetails.izborniPredmeti")}
                    value={item.electiveSubjects
                      ?.filter((v) => v.parentId === secondaryInstitution?.id)
                      .map((v) => v.id)}
                    onChange={handleSecondaryInstituteElectiveSubjectChange}
                    selectData={codeBookToSelectData(
                      secondaryInstitution?.electiveSubjects
                    )}
                    errorText={
                      errors?.find((e) => e.propertyName === "ElectiveSubjects")
                        ?.message
                    }
                  />
                </Col>
              </Row>
            </FormCard>
            <FormCard cardTitle={t("request.contactInformation")}>
              {secondaryInstitution
                ? getInstitutionContactInformation(secondaryInstitution)
                : ""}
            </FormCard>
          </>
        )}
      </FormCard>
    </FormCard>
  );
};
